import { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } from '../../config/consts';
import Modal from '../Modal';
import emailjs from '@emailjs/browser';
import success_icon from '../../assets/icons/success-icon.jpg';
import warning_icon from '../../assets/icons/warning-icon.jpg';
import useInView from '../../hooks/useInView';
import validators from './formValidators';
import { useDynamicForm } from '../../hooks/useDynamicForm';
import {
    modalData,
    modalReducer,
    CLOSE_MODAL,
    SET_ERROR_MODAL,
    SET_SUCCESS_MODAL,
    SHOW_MODAL,
} from '../../reducers/modalReducer';

const initialValues = { name: '', email: '', phone: '', message: '' };

const Contact = () => {
    const { i18n, t } = useTranslation();
    const [ref, isInView] = useInView();

    const { errors, bind, validate } = useDynamicForm({
        initialValues,
        validators,
    });

    const [{ showModal, isLoading, isSuccess, modalTitle, modalBody, modalButtonText }, dispatch] = useReducer(
        modalReducer,
        modalData
    );

    const isRtl = i18n.language === 'he';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            dispatch({ type: SHOW_MODAL });
            emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, e.target, PUBLIC_KEY).then(
                (result) => {
                    dispatch({
                        type: SET_SUCCESS_MODAL,
                        modalTitle: t('success-modal-title'),
                        modalBody: t('success-modal-body'),
                        modalButtonText: t('close'),
                    });
                },
                (error) => {
                    dispatch({
                        type: SET_ERROR_MODAL,
                        modalTitle: t('error-modal-title'),
                        modalBody: t('error-modal-body'),
                        modalButtonText: t('close'),
                    });
                }
            );
        }
    };

    const handleClose = () => {
        dispatch({ type: CLOSE_MODAL });
    };

    return (
        <section id='contact'>
            <div className='container' ref={ref}>
                <div className='text-center'>
                    <h3 className='section-title'>
                        <span className='text-tertiary'>{t('contact-title.part1')}</span>
                        <span className='text-primary'>{t('contact-title.part2')}</span>
                    </h3>
                </div>
                <form id='contactForm' onSubmit={handleSubmit} className={`${isInView ? 'fade-up' : ''}`}>
                    <div className='mb-5'>
                        <div className='input-wrapper'>
                            <input {...bind('name')} type='text' placeholder={t('contact-name-placeholder')}></input>
                            {errors.name && <div className='invalid-input'>{t(errors.name)}</div>}
                        </div>
                        <div className='input-wrapper'>
                            <input {...bind('email')} type='email' placeholder={t('contact-email-placeholder')}></input>
                            {errors.email && <div className='invalid-input'>{t(errors.email)}</div>}
                        </div>
                        <div className='input-wrapper'>
                            <input {...bind('phone')} type='tel' placeholder={t('contact-phone-placeholder')}></input>
                            {errors.phone && <div className='invalid-input'>{t(errors.phone)}</div>}
                        </div>
                        <div className='textarea-wrapper'>
                            <textarea {...bind('message')} placeholder={t('contact-message-placeholder')}></textarea>
                            {errors.message && <div className='invalid-input'>{t(errors.message)}</div>}
                        </div>
                    </div>
                    <div className='d-none' id='submitErrorMessage'>
                        <div className='text-center text-danger mb-3'>{t('error-sending-message')}</div>
                    </div>
                    <div className='text-center'>
                        <button
                            className={`btn btn-primary btn-rounded text-uppercase`}
                            id='submitButton'
                            type='submit'
                        >
                            {t('send-message')}
                        </button>
                    </div>
                    <Modal
                        classes='modal'
                        show={showModal}
                        centered
                        handleClose={handleClose}
                        btnPrimaryHandler={handleClose}
                        title={modalTitle}
                        body={modalBody}
                        btnPrimaryText={modalButtonText}
                        isRtl={isRtl}
                        statusIcon={isSuccess ? success_icon : warning_icon}
                        isLoading={isLoading}
                    />
                </form>
            </div>
        </section>
    );
};

export default Contact;
